"use client"

import { useEffect, useLayoutEffect, useState } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { AlignLeftIcon, SearchIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { BasketData } from "@/lib/intershop/interfaces/basket/basket"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { ApplicationSettings } from "@/lib/settings/application-settings"
import { Category } from "@/lib/storefront/category/category"
import { cn } from "@/lib/utils"
import { MultilinkStoryblok, UbHeaderLinkStoryblok, UbHeaderStoryblok } from "@/components/storyblok/component-types"
import HeaderLogo from "@/components/storyblok/header/HeaderLogo"
import StoryblokAccountPopupClient from "@/components/storyblok/header/StoryblokAccountPopupClient"
import StoryblokMenuBar from "@/components/storyblok/header/StoryblokMenuBar"
import StoryblokPrivateCustomerToggle from "@/components/storyblok/header/StoryblokPrivateCustomerToggle"
import StoryblokSearchBox from "@/components/storyblok/header/StoryblokSearchBox"
import StoryblokServicePhone from "@/components/storyblok/header/StoryblokServicePhone"
import StoryblokShoppingBasketPopup from "@/components/storyblok/header/StoryblokShoppingBasketPopup"
import StoryblokSwissLocaleSwitch from "@/components/storyblok/header/StoryblokSwissLocaleSwitch"
import StoryblokValueProposition from "@/components/storyblok/header/StoryblokValueProposition"

export interface UbHeaderLinkStoryblokExtended extends UbHeaderLinkStoryblok {
  multilink?: MultilinkStoryblok
  label?: string
}

interface UBHeaderProps {
  oci: boolean
  blok: UbHeaderStoryblok
  applicationSettings: ApplicationSettings
  categories: Category[]
  navigation: UbHeaderLinkStoryblokExtended[]
  userData?: UserData
  isB2BCustomer: boolean
  isAnonymous: boolean
  basket?: BasketData
  sbLocale: string
  isoLocale: string
}

export default function UBHeaderClient({
  oci,
  blok,
  applicationSettings,
  categories,
  navigation,
  userData,
  isB2BCustomer,
  isAnonymous,
  basket,
  sbLocale,
  isoLocale,
  ...restProps
}: UBHeaderProps) {
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false)
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false)

  const { setIsB2BCustomer, setIsOCI, setBasketData } = useUbStorefrontState()

  const t = useTranslations("storefront")

  useLayoutEffect(() => {
    setIsB2BCustomer(isB2BCustomer)
    setIsOCI(oci)
  }, [setIsB2BCustomer, isB2BCustomer, setIsOCI, oci])

  useEffect(() => {
    if (basket) {
      // if no basket exists, then we treat is as empty
      setBasketData(basket)
    }
  }, [basket, setBasketData])

  const onMobileMenuClick = () => {
    const localIsMobileMenuVisible = !isMobileMenuVisible
    setIsMobileMenuVisible(localIsMobileMenuVisible)
    document.body.style.overflowY = localIsMobileMenuVisible ? "hidden" : "visible"
  }

  return (
    /*Why w-screen? See:
     https://github.com/radix-ui/primitives/issues/1496 and
     https://www.reddit.com/r/reactjs/comments/1fjcwkh/comment/lnt3a0j/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button*/
    <div
      className={cn("fixed z-[151] w-screen bg-white", {
        "h-screen overflow-y-auto pb-36": isMobileMenuVisible,
      })}
    >
      <div
        className={cn(
          "mx-auto flex h-14 max-w-7xl items-start border-b border-gray-200 px-4 py-2 sm:px-6 lg:h-[90px] lg:border-0 lg:px-8",
          {
            "hidden lg:block": isMobileSearchVisible,
          }
        )}
      >
        <div className="flex w-full items-center gap-4 lg:items-start">
          <HeaderLogo logo={blok.logo} subtext={blok.subtext} sbLocale={sbLocale} className={""} oci={oci} />
          <div className={cn("flex grow flex-col justify-center gap-2")}>
            <div className={"hidden h-7 flex-row border-b border-gray-200 transition-all lg:flex"}>
              <div className={"flex w-full flex-row items-center justify-between gap-1 pb-1"}>
                <div className="mr-2 flex grow flex-row items-center justify-between gap-2">
                  {oci ? (
                    <>
                      {blok.benefit_oci_1 && (
                        <StoryblokValueProposition text={blok.benefit_oci_1} link={blok.benefit_link_oci_1?.url} />
                      )}
                      {blok.benefit_oci_2 && (
                        <StoryblokValueProposition
                          text={blok.benefit_oci_2}
                          link={blok.benefit_link_oci_2?.url}
                          className={"hidden lg:flex"}
                        />
                      )}
                      {blok.benefit_oci_3 && (
                        <StoryblokValueProposition
                          text={blok.benefit_oci_3}
                          link={blok.benefit_link_oci_3?.url}
                          className="hidden xl:flex"
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {isB2BCustomer ? (
                        <>
                          {blok.benefit_b2b_1 && (
                            <StoryblokValueProposition text={blok.benefit_b2b_1} link={blok.benefit_link_b2b_1?.url} />
                          )}
                          {blok.benefit_b2b_2 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2b_2}
                              link={blok.benefit_link_b2b_2?.url}
                              className={"hidden lg:flex"}
                            />
                          )}
                          {blok.benefit_b2b_3 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2b_3}
                              link={blok.benefit_link_b2b_3?.url}
                              className="hidden xl:flex"
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {blok.benefit_b2c_1 && (
                            <StoryblokValueProposition text={blok.benefit_b2c_1} link={blok.benefit_link_b2c_1?.url} />
                          )}
                          {blok.benefit_b2c_2 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2c_2}
                              link={blok.benefit_link_b2c_2?.url}
                              className={"hidden lg:flex"}
                            />
                          )}
                          {blok.benefit_b2c_3 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2c_3}
                              link={blok.benefit_link_b2c_3?.url}
                              className="hidden xl:flex"
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className={"flex items-center justify-between gap-1 lg:min-w-96 xl:gap-2"}>
                  <StoryblokServicePhone blok={blok} sbLocale={sbLocale} />
                  {!oci && <StoryblokPrivateCustomerToggle isB2BCustomer={isB2BCustomer} userData={userData} />}
                </div>
              </div>
            </div>
            <div className="flex h-9 grow flex-row items-center justify-end xl:gap-2">
              <div className="size-full grow max-lg:hidden">
                <StoryblokSearchBox isoLocale={isoLocale} {...restProps} />
              </div>
              <div className={"flex grow items-center justify-between max-lg:max-w-40 lg:min-w-96 xl:gap-2"}>
                <button
                  aria-label={t("header.search_placeholder")}
                  onClick={() => setIsMobileSearchVisible(true)}
                  className="flex h-full flex-row items-center justify-center text-gray-600 lg:hidden"
                >
                  <SearchIcon className={"h-[20px] text-black"} />
                </button>
                <StoryblokAccountPopupClient blok={blok} userData={userData} sbLocale={sbLocale} />
                <StoryblokShoppingBasketPopup
                  isoLocale={isoLocale}
                  blok={blok}
                  applicationSettings={applicationSettings}
                  isB2B={isB2BCustomer}
                  isAnonymous={isAnonymous}
                  userData={userData}
                />
                <StoryblokSwissLocaleSwitch isoLocale={isoLocale} {...restProps} />
                <button
                  aria-label={t("header.menu")}
                  onClick={onMobileMenuClick}
                  className="flex h-full flex-row items-center justify-center font-semibold text-gray-600 lg:hidden"
                >
                  <AlignLeftIcon className={"h-[20px] text-black"} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isMobileSearchVisible && (
        <div className="p-3.5 lg:hidden">
          <StoryblokSearchBox isoLocale={isoLocale} onClose={() => setIsMobileSearchVisible(false)} {...restProps} />
        </div>
      )}

      <StoryblokMenuBar
        isB2BCustomer={isB2BCustomer}
        blok={blok}
        categories={categories}
        navigation={navigation}
        isMobileVisible={isMobileMenuVisible}
        userData={userData}
        sbLocale={sbLocale}
        oci={oci}
        labelAllCategories={t("header.all_categories")}
      />
    </div>
  )
}
